import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  AiOutlineEnvironment,
  AiOutlineHeart,
  AiOutlineShoppingCart,
  AiOutlineUser,
} from "react-icons/ai";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import { setUser } from "../../store/reducers/authReducer";
import { useUserMeQuery } from "../../store/services/authService";
import { useUserAddressesQuery } from "../../store/services/checkoutService";
import { useGetOrdersQuery } from "../../store/services/userOrdersService";
import { toman, toPersianNumber } from "../../utils/persian";
import { productUrl } from "../../utils/productUrl";
import { orderDetailUrl } from "../../utils/orderUrl";

const productImage = (item) => {
  const image = item?.variant?.image || item?.product?.main_image || item?.product?.gallery?.[0]?.url || item?.product?.gallery?.[0]?.image;
  if (!image) return "";
  if (String(image).startsWith("http")) return image;
  return `/storage/${image}`;
};

const Dashboard = () => {
  const { user } = useSelector((state) => state.authReducer);
  const { data: meData } = useUserMeQuery(
    user?.id || "current-session",
    { refetchOnMountOrArgChange: true }
  );
  const { data: addressesData, isFetching: addressesLoading } = useUserAddressesQuery();
  const { data: ordersData, isFetching: ordersLoading } = useGetOrdersQuery(1);

  const dispatch = useDispatch();

  const addresses = addressesData?.addresses || [];
  const orders = ordersData?.orders?.data || [];
  const ordersTotal = ordersData?.orders?.total || 0;
  const defaultAddress = addresses.find((item) => item.is_default) || addresses[0];

  const userName = user?.name && user.name !== "کاربر جوتی" ? user.name : "دوست جوتی";
  const firstName = useMemo(() => String(userName).split(" ")[0] || "دوست جوتی", [userName]);

  useEffect(() => {
    if (meData?.user) {
      dispatch(setUser(meData.user));
    }
  }, [meData?.user, dispatch]);

  return (
    <>
      <Nav />
      <Toaster position="top-right" reverseOrder={false} />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero">
                <div>
                  <span className="account-kicker">پروفایل کاربری</span>
                  <h1>{firstName} عزیز، خوش آمدی</h1>
                  <p>سفارش‌ها، آدرس‌ها و اطلاعات حساب شما اینجا مرتب شده‌اند.</p>
                </div>

                <Link to="/settings" className="account-hero-action">
                  تکمیل حساب
                </Link>
              </div>

              <div className="account-stat-grid">
                <Link to="/orders" className="account-stat-card">
                  <span className="account-stat-icon">
                    <AiOutlineShoppingCart size={24} />
                  </span>
                  <small>سفارش‌ها</small>
                  <strong>{ordersLoading ? "..." : toPersianNumber(ordersTotal)}</strong>
                </Link>

                <Link to="/addresses" className="account-stat-card">
                  <span className="account-stat-icon">
                    <AiOutlineEnvironment size={24} />
                  </span>
                  <small>آدرس‌ها</small>
                  <strong>{addressesLoading ? "..." : toPersianNumber(addresses.length)}</strong>
                </Link>

                <Link to="/wishlist" className="account-stat-card">
                  <span className="account-stat-icon">
                    <AiOutlineHeart size={24} />
                  </span>
                  <small>علاقه‌مندی‌ها</small>
                  <strong>لیست من</strong>
                </Link>
              </div>

              <div className="account-grid">
                <section className="account-card account-card-main">
                  <div className="account-card-head">
                    <div>
                      <span>اطلاعات کاربری</span>
                      <h2>حساب شما</h2>
                    </div>
                    <AiOutlineUser size={25} />
                  </div>

                  <div className="account-info-list">
                    <div>
                      <span>نام</span>
                      <strong>{user?.name && user.name !== "کاربر جوتی" ? user.name : "ثبت نشده"}</strong>
                    </div>
                    <div>
                      <span>موبایل</span>
                      <strong className="ltr">{user?.mobile || "-"}</strong>
                    </div>
                  </div>

                  <div className="account-card-actions">
                    <Link to="/settings">ویرایش اطلاعات</Link>
                  </div>
                </section>

                <section className="account-card">
                  <div className="account-card-head">
                    <div>
                      <span>آدرس پیش‌فرض</span>
                      <h2>ارسال سفارش</h2>
                    </div>
                    <AiOutlineEnvironment size={25} />
                  </div>

                  {defaultAddress ? (
                    <div className="account-address-mini">
                      <strong>{defaultAddress.receiver_name}</strong>
                      <p>
                        {[defaultAddress.province, defaultAddress.city, defaultAddress.address]
                          .filter(Boolean)
                          .join("، ")}
                      </p>
                      <Link to="/addresses">مدیریت آدرس‌ها</Link>
                    </div>
                  ) : (
                    <div className="account-empty-mini">
                      <p>هنوز آدرسی ذخیره نشده است.</p>
                      <Link to="/addresses">افزودن آدرس</Link>
                    </div>
                  )}
                </section>
              </div>

              <section className="account-card">
                <div className="account-card-head">
                  <div>
                    <span>سابقه سفارش‌ها</span>
                    <h2>آخرین خریدها</h2>
                  </div>
                  <AiOutlineShoppingCart size={25} />
                </div>

                {ordersLoading ? (
                  <div className="account-loading">در حال دریافت...</div>
                ) : orders.length ? (
                  <div className="account-history-list">
                    {orders.slice(0, 4).map((order) => (
                      <article className="account-history-row" key={order.id}>
                        <div className="account-history-head">
                          <div>
                            <span>{order.order_number}</span>
                            <strong>{toman(order.total || 0)}</strong>
                          </div>
                          <Link to={orderDetailUrl(order)}>جزئیات بیشتر</Link>
                        </div>

                        <div className="account-history-products">
                          {(order.items || []).slice(0, 4).map((item) => (
                            <Link to={productUrl(item?.product || item?.product_id, item?.product_name)} className="account-mini-product" key={item.id}>
                              <div className="account-mini-product-img">
                                {productImage(item) ? <img src={productImage(item)} alt={item.product_name || ""} /> : null}
                              </div>
                              <div>
                                <strong>{item.product_name}</strong>
                                <span>{toman(item.total_price || item.unit_price || 0)}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="account-empty-mini">
                    <p>هنوز سفارشی ثبت نشده است.</p>
                    <Link to="/products">مشاهده محصولات</Link>
                  </div>
                )}
              </section>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default Dashboard;
